import {Injectable} from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {environment} from '../../../../src/environments/environment';
import {ObjectModel} from './object.model';
import {BehaviorSubject, EMPTY, firstValueFrom, Observable, of} from 'rxjs';
import {catchError, shareReplay} from 'rxjs/operators';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import {CalcparamModel} from '../calcparam/calcparam.model';
import {ReactiveServiceWithFilters} from '../reactive-service-with-filters.abstract';
import {HierarchyModel} from '../hierarchy/hierarchy.model';

/**
 * Управляет тремя опциональными блоками данных, которые backend умеет не отдавать
 * в ответе GET db/object, если они форме не нужны:
 * - withTags — paramtags;
 * - withStates — worstparamstate, worstparamcount, states;
 * - withControllers — controllersTotalCount, controllersOfflineCount.
 * Каждый флаг по умолчанию на backend true; сюда передаём только то, что хотим явно выключить.
 */
export interface ObjectsFetchOptions {
  withTags?: boolean;
  withStates?: boolean;
  withControllers?: boolean;
}

@Injectable()
export class ObjectsApiService extends ReactiveServiceWithFilters  {


  timeout = 160 * 60 * 10000; // 5 минут

  private baseUrl = environment.backEndUrlPrefix + '/' + 'db/object';
  private analyticUrl = environment.backEndUrlPrefix + '/' + 'analytic/events';

  private selectedParamsSubject = new BehaviorSubject<any[]>([]);
  selectedParams$ = this.selectedParamsSubject.asObservable();

  updateSelectedParams(params: any[]) {
    this.selectedParamsSubject.next(params);
  }


  constructor(private http: HttpClient) {
    super();
  }

  loadAll(options?: ObjectsFetchOptions) {
    const timeout = 5 * 60 * 10000;
    const key = 'loadAll' + this.fetchOptionsCacheKey(options);
    const cache = this.getSnapshot(key, timeout);
    if (!cache) {
      this.request(key, this.http.get<ObjectModel[]>(this.baseUrl, {params: this.buildFetchParams(options)}));
    }
  }

  getAnalyticEvents(objectIds: number[]): Observable<any[]> {
    // todo: сделать настройку для соединений
    return this.http.get<any[]>(this.analyticUrl,
      {params: {
          objectId: objectIds,
          limit: 8
        }});
  }
  getAll(filter?: any, options?: ObjectsFetchOptions) {
    if (filter) {
      return this.http.get<ObjectModel[]>(this.baseUrl,
        {params: {
            worstSysStateId: filter.sysParamStates.map(id => String(id)),
            state: filter.objectStates.map(id => String(id)),
            objectTypeId: filter.objectTypes.map(id => String(id)),
            paramTag: filter.labels.map(obj => obj.name),
            ...this.buildFetchParams(options),
        }}).pipe(
        shareReplay(1),
        catchError(err => EMPTY));
    }
    return this.http.get<ObjectModel[]>(this.baseUrl, {params: this.buildFetchParams(options)}).pipe(
      shareReplay(1),
      catchError(err => EMPTY));

  }

  /** Сериализует набор флагов в суффикс для ключа снапшота, чтобы forms с разными
   * withTags/withStates/withControllers не путали кэш друг друга в рамках одного instance сервиса. */
  private fetchOptionsCacheKey(options?: ObjectsFetchOptions): string {
    if (!options) {
      return '';
    }
    return `:${options.withTags ?? true}:${options.withStates ?? true}:${options.withControllers ?? true}`;
  }

  private buildFetchParams(options?: ObjectsFetchOptions): Record<string, boolean> {
    const params: Record<string, boolean> = {};
    if (options?.withTags !== undefined) {
      params.withTags = options.withTags;
    }
    if (options?.withStates !== undefined) {
      params.withStates = options.withStates;
    }
    if (options?.withControllers !== undefined) {
      params.withControllers = options.withControllers;
    }
    return params;
  }


  getAllShortest() {
    return this.http.get<ObjectModel[]>(this.baseUrl + '/shortest');
  }

  deleteGroup(ids: number[]) {
    return this.http.delete<string>(this.baseUrl, {params: {id: ids.map(id => String(id))}});
  }

  byRubric(id, options?: ObjectsFetchOptions) {
    return this.http.get<ObjectModel[]>(`${this.baseUrl}/child/objrubric/${id}`, {params: this.buildFetchParams(options)});
  }

  byId(object_id): Observable<ObjectModel> {
    return this.http.get<ObjectModel>(this.baseUrl + `/${object_id}`);
  }

  getCDP(object_id, params = {}) {
    return this.http.get<any>(this.baseUrl + `/${object_id}/cdp`, {params});
  }

  getDashboards(object_id, params = {}) {
    return this.http.get<any>(this.baseUrl + `/${object_id}/dashboard`, {params});
  }

  getAttributes(object_id, params = {}) {
    //return this.http.get<any>(this.baseUrl + `/${object_id}/attr`, {params});
    return this.http.get<any>(`${environment.backEndUrlPrefix}/object_attr?objectId=${object_id}`, {params});
  }

  getControllers(object_id, params = {}) {
    return this.http.get<any>(environment.backEndUrlPrefix + `/db/controller/child/object/${object_id}`, {params});
  }

  updateGroup(ids: number[], data): Observable<ObjectModel[]> {
      return this.http.patch<HierarchyModel[]>(this.baseUrl, data, {params: {id: ids.map(id => id.toString())}});
  }

  resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<CalcparamModel[]> | Promise<CalcparamModel[]> | CalcparamModel[] {
    // Маршрут может указать в data.objectsFetchOptions, какие блоки (withTags/withStates/withControllers)
    // форме не нужны — см. object-routing.module.ts.
    const options: ObjectsFetchOptions | undefined = route.data.objectsFetchOptions;
    // Если загружаем в фоне, то быстро переходим к отрисовке страницы
    if (route.data.shadowLoad) {
      const arr = [];
      firstValueFrom(this.getAll(undefined, options)).then(async (data) => {
        if (Array.isArray(data)) {
          data.forEach(item => arr.push(item));
        }
        this.updateData(data);
      });
      return of(arr);
    }
    return this.getAll(undefined, options);
  }

}
